// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import type { DocumentNode } from '@apollo/client';
import { CombinedGraphQLErrors, CombinedProtocolErrors } from '@apollo/client/errors';
import { Answer } from '../../CopilotContext';
import { mergeToolCallsIntoAnswers, toToolCalls } from './ToolCalls/toolCalls';

export type StreamChunk = {
    content: string | null;
    chatId: string | null;
    // CopilotMessages.id, set only on the final "committed" chunk of a persisted turn
    id: string | null;
    ableToAnswer: boolean | null;
    // On the committed chunk the turn's batch; on a chunk without an id the one call
    // of the batch being run that has just settled
    toolCalls: Parameters<typeof toToolCalls>[0];
    // Localized, set only on the final chunk of a failed stream
    error: string | null;
};

// Initial-payload data of a streamed mutation; subsequent incremental chunks are typed by
// GraphQL17Alpha9Handler.TypeOverrides
export type StreamPayload = {
    // An answer's
    error?: string | null;
    // A run's refusal or failure
    runError?: string | null;
    ranToolCalls?: Parameters<typeof toToolCalls>[0];
    contentChunks?: (StreamChunk | null)[] | null;
} | null | undefined;

export type AnswerPayload = {
    content: string | null;
    error: string | null;
    ableToAnswer: boolean;
    chatId: string | null;
    id: string | null;
    toolCalls: Parameters<typeof toToolCalls>[0];
};

export type GraphQlErrorLike = { message: string; extensions?: Record<string, unknown> | null };

/**
 * The send refusal: a PleskUserException, which ErrorProcessor gives the `user` category. A
 * service failure never takes that form - it becomes an ordinary answer error.
 */
export const userErrorMessage = (errors: readonly GraphQlErrorLike[] | undefined): string | null => errors
    ?.find(error => error.extensions?.category === 'user')?.message ?? null;

/**
 * Whether a failed request never got its response through - the network, a gateway, a cut
 * body - rather than being answered with GraphQL errors: only then may the server still be
 * going on with it.
 */
export const isTransportError = (error: unknown): boolean => !CombinedGraphQLErrors.is(error) && !CombinedProtocolErrors.is(error);

export const isSettleChunk = (chunk: StreamChunk): boolean => !chunk.error && chunk.id == null && !!chunk.toolCalls?.length;

/**
 * Applies one stream chunk - of an answer stream or of a run stream - to the answers:
 *
 * - a settled call (toolCalls, no id) is merged into the answer that issued it;
 * - prose is appended to the turn in progress, created at the first such chunk;
 * - the committed chunk sets that turn's id and replaces its batch, and ends it;
 * - an error chunk sets that turn's error, and ends it.
 */
export const applyStreamChunk = (answers: Answer[], chunk: StreamChunk, progressKey: string, chatId?: string): Answer[] => {
    if (isSettleChunk(chunk)) {
        return mergeToolCallsIntoAnswers(answers, toToolCalls(chunk.toolCalls) ?? []);
    }
    const index = answers.findIndex(answer => answer.key === progressKey);
    if (chunk.id != null && answers.some(answer => answer.id === chunk.id && answer.key !== progressKey)) {
        // A history reload already brought in the committed row: the turn in progress is a
        // duplicate of it
        return index >= 0 ? answers.filter(answer => answer.key !== progressKey) : answers;
    }
    const current: Answer = index >= 0 ? answers[index] : {
        key: progressKey,
        id: null,
        role: 'assistant',
        streaming: true,
    };
    let next: Answer;
    if (chunk.error) {
        next = { ...current, error: chunk.error, streaming: false };
    } else {
        next = {
            ...current,
            content: chunk.content ? (current.content ?? '') + chunk.content : current.content,
            chat_id: current.chat_id ?? chunk.chatId ?? chatId,
            able_to_answer: chunk.ableToAnswer == null ? current.able_to_answer : Boolean(current.able_to_answer) || chunk.ableToAnswer,
        };
        if (chunk.id != null) {
            // The id arrives in the committed chunk; the key never changes
            next = { ...next, id: chunk.id, toolCalls: toToolCalls(chunk.toolCalls), streaming: false };
        }
    }
    if (index < 0) {
        return [...answers, next];
    }
    const copy = [...answers];
    copy[index] = next;
    return copy;
};

export const answerFromPayload = (data: AnswerPayload, key: string, chatId: string | undefined): Answer => ({
    key,
    id: data.id ?? null,
    role: 'assistant',
    content: data.content ?? undefined,
    error: data.error ?? undefined,
    able_to_answer: data.ableToAnswer ?? false,
    chat_id: data.chatId ?? chatId,
    toolCalls: toToolCalls(data.toolCalls),
});

export type StreamOutcome = {
    received: number;
    failed: boolean;
    // Whether it failed because the response did not get through
    lostInTransport: boolean;
    // Whether the server ended the stream with an error chunk
    errorChunk: boolean;
    refusal: string | null;
    initialError: string | null;
    progressKey: string | null;
};

export type ConsumeStream = (
    query: DocumentNode,
    variables: Record<string, unknown>,
    rootField: 'genCopilotAnswer' | 'runCopilotToolCalls',
    options: {
        onInitial?: (initial: NonNullable<StreamPayload>) => void;
        focusInput: boolean;
        // A run belongs to its conversation: once another one is shown, its chunks are dropped
        chatGuard?: string;
    },
) => Promise<StreamOutcome>;
